import {
  Box, Grid, Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer, TableHead,
  TableRow, Typography,
} from '@mui/material';
import React from 'react';
import {SectionHeader} from './header-section';
import {
  CrossoverSelectionEnum, FacilityLevelOfCare,
  FacilityLevelOfCareWithCrossoverDays,
} from '@finpay-estimation/estimation-types';
import _ from 'lodash';
import {TableColumn} from '../../../../shared/model/table-column';
import {formatNumberToUSD} from '@finpay-development/shared-platform/lib/esm/shared/calculators';
import {sectionPaperStyling} from './estimate-view-v4';

const CARE_PLAN_COLUMN_WIDTH = 180;
type CarePlanRow = {
  location: string;
  code: string;
  covered: string;
  coinsurance: string;
  copay: string;
  sessions: string;
  charges: string;
};
const carePlanRows: CarePlanRow[] = [
  {
    location: 'IOP Days',
    code: 'H2035',
    covered: '$15,000.00',
    coinsurance: '$0.00',
    copay: '$50.00',
    sessions: '30',
    charges: '$15,050.00',
  },
  {
    location: 'PHP Days',
    code: 'H2032',
    covered: '$5,000.00',
    coinsurance: '$0.00',
    copay: '$0.00',
    sessions: '15',
    charges: '$5,000.00',
  },
  {
    location: 'Residential',
    code: 'H0018',
    covered: '$0.00',
    coinsurance: '$0.00',
    copay: '$0.00',
    sessions: '30',
    charges: '$12,500.00',
  },
];
const uncoveredServicesRows = [
  {service: 'Flight', quantity: '1', charges: '$700.00'},
  {service: 'Sober Living', quantity: '30', charges: '$10,000.00'},
];
const uncoveredChargesTotal = '$10,700.00';

export const CarePlanDetailsSection = ({
                                         facilityName,
                                         anticipatedAdmitDate,
                                       }: { facilityName: string, anticipatedAdmitDate: string }) => {
  return (
      <Box mb={4}>
        <SectionHeader title="Care Plan Details"
                       testId="care-plan-details-title"/>
        <TableContainer>
          <Table
              size="small"
              sx={{
                '& td, & th': {borderBottom: 'none'},
                '& tr:first-of-type td': {paddingTop: 0},
              }}
          >
            <TableBody>
              <TableRow>
                <TableCell colSpan={6}>
                  <strong>Facility</strong>
                </TableCell>
                <TableCell align="right">{facilityName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={6}>
                  <strong>Anticipated Admission Date</strong>
                </TableCell>
                <TableCell align="right">{anticipatedAdmitDate}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <TableContainer component={Paper}>
          <Table size="small" className="alternating-row">
            <TableHead>
              <TableRow>
                <TableCell>Level of Care</TableCell>
                <TableCell sx={{width: CARE_PLAN_COLUMN_WIDTH}}>Code</TableCell>
                <TableCell align="right" sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                  Covered
                </TableCell>
                <TableCell align="right" sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                  Co-Insurance
                </TableCell>
                <TableCell align="right" sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                  Co-Pay
                </TableCell>
                <TableCell align="right" sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                  Sessions
                </TableCell>
                <TableCell align="right" sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                  Charges
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{'& td': {borderBottom: 'none'}}}>
              {carePlanRows.map(row => (
                  <TableRow key={`${row.location}-${row.code}`}>
                    <TableCell>{row.location}</TableCell>
                    <TableCell sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                      {row.code}
                    </TableCell>
                    <TableCell align="right"
                               sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                      {row.covered}
                    </TableCell>
                    <TableCell align="right"
                               sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                      {row.coinsurance}
                    </TableCell>
                    <TableCell align="right"
                               sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                      {row.copay}
                    </TableCell>
                    <TableCell align="right"
                               sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                      {row.sessions}
                    </TableCell>
                    <TableCell align="right"
                               sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                      {row.charges}
                    </TableCell>
                  </TableRow>
              ))}
              <TableRow
                  sx={{
                    backgroundColor: '#edf7ed',
                    '& td': {borderTop: '1px solid #d0d0d0'},
                  }}
              >
                <TableCell colSpan={6}>Estimated Adjusted Charges</TableCell>
                <TableCell align="right">$32,550.00</TableCell>
              </TableRow>
              <TableRow sx={{backgroundColor: '#edf7ed'}}>
                <TableCell colSpan={6}>Estimated Length of Stay</TableCell>
                <TableCell align="right">45</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <TableContainer component={Paper} sx={{mt: 3}}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Uncovered Services</TableCell>
                <TableCell align="right" sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                  Quantity
                </TableCell>
                <TableCell align="right" sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                  Charges
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{'& td': {borderBottom: 'none'}}}>
              {uncoveredServicesRows.map(service => (
                  <TableRow key={service.service}>
                    <TableCell>{service.service}</TableCell>
                    <TableCell align="right"
                               sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                      {service.quantity}
                    </TableCell>
                    <TableCell align="right"
                               sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                      {service.charges}
                    </TableCell>
                  </TableRow>
              ))}
              <TableRow
                  sx={{
                    backgroundColor: '#eaf7e4',
                    '& td': {borderTop: '1px solid #d0d0d0'},
                  }}
              >
                <TableCell>
                  Uncovered Charges
                </TableCell>
                <TableCell sx={{width: CARE_PLAN_COLUMN_WIDTH}}></TableCell>
                <TableCell align="right" sx={{width: CARE_PLAN_COLUMN_WIDTH}}>
                  {uncoveredChargesTotal}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" display="block" sx={{mt: 2, ml: 1}}>
          Please note that copay obligations for inpatient treatment are
          typically only due once per unique admission.
        </Typography>

      </Box>
  );
};

export const CarePlanDetailsSectionOld = ({
                                            estimate: {
                                              facilityLevelOfCare,
                                              isPlanYearCrossover,
                                              crossoverSelection,
                                              summary: {
                                                coveredCharges,
                                                unCoveredCharges,
                                                lengthOfStay,
                                              },
                                            },
                                            facilityName,
                                            admissionDate,
                                          }: {
  estimate: {
    facilityLevelOfCare: any
    isPlanYearCrossover: boolean
    crossoverSelection: number
    summary: {
      coveredCharges: number
      unCoveredCharges: number
      lengthOfStay: number
    },
  },
  facilityName: string
  admissionDate: string
}) => {

  const locTableHeaders: TableColumn[] = [
    {id: 'colCode', label: 'Code'},
    {id: 'colLocName', label: 'Level of Care'},
    {id: 'colLocDays', label: 'Days/Sessions'},
    {id: 'colLocDays', label: 'Rate per Day'},
    {id: 'colCovered', label: 'Covered'},
  ];

  const estimatedAdjustedCharges =
      formatNumberToUSD(
          coveredCharges! +
          unCoveredCharges!,
      );

  function LocTable(props: { levelsOfCare: FacilityLevelOfCareWithCrossoverDays[] }) {

    const {levelsOfCare} = props;

    return (
        <React.Fragment>
          <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                ...sectionPaperStyling,
              }}
          >
            <Table
                size="small"
                sx={{
                  '& td': {borderBottom: 'none', paddingY: 1.2},
                }}
            >
              <TableHead>
                <TableRow>
                  {locTableHeaders.map((column, index) => (
                      <TableCell
                          key={column.id}
                      >
                        {column.label}
                      </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {_.sortBy(levelsOfCare, 'sortOrder').
                    filter((loc: FacilityLevelOfCareWithCrossoverDays) => {
                      return loc.locType !== 1;

                    }).
                    map((
                            row: FacilityLevelOfCareWithCrossoverDays,
                            index: number,
                        ) => {
                          const days = isPlanYearCrossover
                              ?
                              crossoverSelection ===
                              CrossoverSelectionEnum.BEFORE &&
                              row?.losDaysAfterCrossover
                                  ? row?.losDaysBeforeCrossover
                                  : row?.losDays
                              :
                              row?.losDays;
                          return (
                              <TableRow key={index}>
                                <TableCell test-id={`loc-code-${index}`}>
                                  {
                                    row?.facilityLevelOfCareCode
                                  }
                                </TableCell>
                                <TableCell test-id={`loc-name-${index}`}>
                                  {
                                    row?.facilityLevelOfCareName
                                  }
                                </TableCell>
                                <TableCell test-id={`loc-days-${index}`}>
                                  {days}
                                </TableCell>
                                <TableCell test-id={`loc-rate-${index}`}>
                                  {row?.pdrRate}
                                </TableCell>
                                <TableCell test-id={`loc-covered-${index}`}>
                                  {row?.isCovered ? (
                                      <>Yes</>
                                  ) : (
                                      <>No</>
                                  )}
                                </TableCell>
                              </TableRow>
                          );
                        },
                    )}
              </TableBody>
            </Table>
          </TableContainer>
        </React.Fragment>
    );
  }

  return (
      <Box mb={4}>
        <Typography
            variant="subtitle2"
            test-id="care-plan-title"
        >
          Care Plan Details
        </Typography>
        <Grid container justifyContent="space-between">
          <Typography
              variant="body2" color="text.secondary"
          >
            Facility
          </Typography>
          <Typography
              variant="body2" color="text.secondary"
          >
            {facilityName}
          </Typography>

        </Grid>
        <Grid container justifyContent="space-between">
          <Typography
              variant="body2" color="text.secondary"
          >
            Anticipated Admission Date
          </Typography>
          <Typography
              variant="body2" color="text.secondary"
          >
            {admissionDate}
          </Typography>
        </Grid>
        <LocTable levelsOfCare={facilityLevelOfCare!.map(
            (loc: FacilityLevelOfCare) => ({
              ...loc,
            })) as FacilityLevelOfCareWithCrossoverDays[]}/>
        <TableContainer
            component={Paper}
            sx={{
              mt: 2,
              ...sectionPaperStyling,

            }}
        >
          <Table
              size="small"
              className="alternating-row"
              sx={{'& td, & th': {borderBottom: 'none'}}}
          >
            <TableBody>
              <TableRow className="py-3" sx={{backgroundColor: '#e6f4e6'}}>
                <TableCell test-id="estimated-adjusted-charges">
                  Estimated Adjusted Charges
                </TableCell>
                <TableCell align="right" test-id="estimated-adjusted-charges">
                  {estimatedAdjustedCharges}
                </TableCell>
              </TableRow>
              <TableRow className="py-3" sx={{backgroundColor: '#e6f4e6'}}>
                <TableCell test-id="estimated-length-of-stay">
                  Estimated Length of Stay
                </TableCell>
                <TableCell align="right" test-id="estimated-length-of-stay">
                  {lengthOfStay}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
  );

};